import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useTheme } from 'styled-components';
import { Drawer } from '@/components/Drawer/Drawer';
import { AngleBackIcon, CategoriesIcon } from '@/components/UI/Icons/Icons';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import { GET_REGIONS } from '@/api/queries/regions.queries';
import { GetRegionsResponse, Region } from '@/api/types/regions.types';
import * as S from '@/components/CatalogueDrawer/CatalogueDrawer.styles';
import {
  SearchInput,
  SearchInputRef,
} from '@/components/SearchInput/SearchInput';
import { NotFound } from '@/components/NotFoundPlaceholder/NotFoundPlaceholder.styles';

interface CitiesDrawerProps {
  isOpen: boolean;
  close: () => void;
  onSelect: (value: Region) => void;
  selected: string;
}

export const CitiesDrawer: React.FC<CitiesDrawerProps> = ({
  isOpen,
  close,
  onSelect,
  selected,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<SearchInputRef>(null);

  useEffect(() => {
    searchInputRef.current?.clear();
  }, [isOpen]);

  const { data: regions } = useQuery<GetRegionsResponse>(GET_REGIONS);

  const cities = regions?.regions.filter(c =>
    c.name.toLowerCase().includes(query.toLowerCase())
  );

  function handleCitySelect(value: Region) {
    onSelect(value);
    close();
  }

  return (
    <Drawer
      isOpen={isOpen}
      title={t('common.city')}
      left={{ icon: <AngleBackIcon />, onClick: close }}
      background={theme.palette.primary.background}
      hasBottomBorder={false}
      hasBottomMargin={false}
      subtitle={
        <S.Container>
          <SearchInput
            ref={searchInputRef}
            onSearch={setQuery}
            leftIcon={<CategoriesIcon fill={theme.palette.tertiary.labels} />}
            hasPadding={false}
            hasBorderBottom={false}
            customPaddingTop={12}
            customPaddingBottom={12}
          />
        </S.Container>
      }
    >
      {cities?.length ? (
        <S.Container>
          {cities.map(city => (
            <Checkbox
              onChangeHook={() => handleCitySelect(city)}
              key={city.name}
              value={city.name === selected}
              label={city.name}
              appearance="radio"
              size="lg"
            />
          ))}
        </S.Container>
      ) : (
        <NotFound>{t('common.nothing_found')}</NotFound>
      )}
    </Drawer>
  );
};
